import React, { useState, useContext, useEffect } from "react";
import 'bootstrap/dist/css/bootstrap.min.css';
import { Routes, Route, useNavigate, Navigate } from "react-router-dom";
import {
  FaSignOutAlt,
  FaUsers,
  FaBars,
  FaHome,
  FaMoneyBillWave,
  FaShoppingCart,
  FaUser,
  FaPlus,
  FaList,
  FaBox,
  FaBoxes,
  FaExchangeAlt,
  FaSpinner,
  FaArrowLeft,
  FaCog,
  FaStore,
  FaHandHoldingUsd,
  FaUniversity,
  FaClipboardList,
} from "react-icons/fa";

// Importations de pages
import Welcome from "./pages/Welcome";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Home from "./pages/Home";
import AddExpense from "./pages/AddExpense";
import ExpenseList from "./pages/ExpenseList";
import SalesManager from "./pages/SalesManager";
import Gestionnaire from "./pages/Gestionnaire";
import Product from "./pages/Product";
import Stock from "./pages/Stocks";
import Clients from "./pages/Clients";
import ClientsDebiteurs from "./pages/ClientsDebiteurs";
import SalesList from "./pages/SalesList";
import Encaissement from "./pages/Encaissement";
import Transfert from "./pages/Transfert";
import ForgotPassword from "./pages/ForgotPassword";
import VerifyCode from "./pages/VerifyCode";
import ResetPassword from "./pages/ResetPassword";
import ApportsExploitant from "./pages/ApportsExploitant"; 
import SiteSelector from "./pages/SiteSelector";
import Settings from "./pages/Settings";
import ArchiveVentes from "./pages/ArchiveVentes";
import ArchiveDepenses from "./pages/ArchiveDepenses";
import ArchiveDebiteurs from "./pages/ArchiveDebiteurs";
import Banque from "./pages/Banque"; 

import { AuthContext, AuthProvider } from "./pages/AuthContext";
import "./App.css";

const LOGO_PATH = "/MaBoutik.png"; 

function AppContent() {
  const { user, logout, currentSite, selectSite } = useContext(AuthContext);
  const navigate = useNavigate();
  const isLoggedIn = !!user?.token;
  const typeUser = user?.type_user;
  const isGestionnaire = typeUser === "gestionnaire";
  const isEntreprise = typeUser === "entreprise";

  const entrepriseId = user?.entrepriseId;
  const entrepriseNom = user?.entrepriseNom;

  const gestionnaireId = isGestionnaire ? user?.id : null;
  const gestionnaireDateCreation = isGestionnaire ? user?.date_creation : null;

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 768 && isEntreprise);
  const [activeMainMenu, setActiveMainMenu] = useState(null);
  const [activeSubItem, setActiveSubItem] = useState(null);
  const [mobileStep, setMobileStep] = useState("main");
  const [desktopStep, setDesktopStep] = useState("main");
  const [selectedSite, setSelectedSite] = useState(currentSite);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [mobileTitle, setMobileTitle] = useState("MaBoutik");

  const ventesEntrepriseSite = [
    { id: "products", label: "Produits", icon: <FaBox />, color: "orange" },
    { id: "stocks", label: "Stocks", icon: <FaBoxes />, color: "purple" },
    { id: "sales-list", label: "Liste des ventes", icon: <FaList />, color: "green" },
    { id: "clients-debiteurs", label: "Clients débiteurs", icon: <FaUsers />, color: "indigo" },
    { id: "archive-debiteurs", label: "Archives Débiteurs", icon: <FaClipboardList />, color: "cyan" },
    { id: "archive-ventes", label: "Archives Ventes", icon: <FaClipboardList />, color: "teal" },
  ];
  
  const ventesGestionnaire = [
    { id: "stocks", label: "Stocks", icon: <FaBoxes />, color: "purple" },
    { id: "clients", label: "Clients", icon: <FaUsers />, color: "indigo" },
    { id: "sales", label: "Ajout Ventes", icon: <FaShoppingCart />, color: "green" },
    { id: "sales-list", label: "Liste des ventes", icon: <FaList />, color: "green" },
    { id: "clients-debiteurs", label: "Clients débiteurs", icon: <FaUsers />, color: "indigo" },
    { id: "archive-debiteurs", label: "Archives Débiteurs", icon: <FaClipboardList />, color: "cyan" },
    { id: "transfert", label: "Transfert", icon: <FaExchangeAlt />, color: "orange" },
  ];

  const labels = {
    ventes: isEntreprise ? ventesEntrepriseSite : ventesGestionnaire,
    depenses: [
      { id: "add-expense", label: "Nouvelle Dépense", icon: <FaPlus />, color: "red" },
      { id: "expenses", label: "Mes Dépenses", icon: <FaList />, color: "teal" },
      { id: "archive-depenses", label: "Archives Dépenses", icon: <FaBox />, color: "cyan" },
    ],
    gestion: [
      { id: "encaissement", label: "Encaissement", icon: <FaMoneyBillWave />, color: "red" },
      { id: "managers", label: "Gestionnaires", icon: <FaUsers />, color: "blue" },
    ],
    operationMain: [
      { id: "apports-exploitant", label: "Apport de l'exploitant", icon: <FaHandHoldingUsd />, color: "blue" },
      { id: "depenses-main", label: "Dépenses", icon: <FaList />, color: "green" },
      { id: "banque", label: "Banque", icon: <FaUniversity />, color: "purple" }, 
      { id: "sites", label: "Boutique", icon: <FaStore />, color: "orange" },
    ],
    afterSite: [
      { id: "ventes", label: "Ventes", icon: <FaShoppingCart />, color: "orange" },
      { id: "gestion", label: "Gestion", icon: <FaUsers />, color: "blue" },
    ],
  };

  const sidebarMenu = [
    { id: "home", label: "Tableau de Bord", icon: <FaHome /> },
    { id: "operations", label: "Opérations", icon: <FaBoxes /> },
    { id: "creerBoutique", label: "Créer Boutiques", icon: <FaCog /> },
  ];

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!isMobile) {
      setSidebarOpen(isEntreprise);
      setMobileStep("main");
    } else {
        setSidebarOpen(false);
    }
  }, [isMobile, isEntreprise]);

  useEffect(() => {
    if (isLoggedIn) {
      setActiveMainMenu(null);
      setActiveSubItem(null);
      setMobileStep("main");
      setDesktopStep("main");
      setMobileTitle("MaBoutik"); 

      if (isGestionnaire) {
        setActiveMainMenu("ventes");
        setMobileStep("labels");
        setDesktopStep("labels");
        setMobileTitle("MaBoutik");
      }
    }
  }, [isLoggedIn, isGestionnaire]);

  const effectiveSiteId = isGestionnaire ? user?.siteId : currentSite?.id;

  const handleLogout = () => {
    setIsLoggingOut(true);
    setTimeout(() => {
      logout();
      navigate("/");
      setIsLoggingOut(false);
    }, 1000);
  };

  const handleMenuClick = (menu) => {
    setActiveMainMenu(menu);
    setSelectedSite(currentSite);
    setActiveSubItem(null);

    const menuLabel = sidebarMenu.find((m) => m.id === menu)?.label || "MaBoutik";
    setMobileTitle(menuLabel);

    if (menu === "home") {
      setActiveSubItem("home-dashboard");
      setMobileStep("labels");
      if (!isMobile) setDesktopStep("component");
      return;
    }

    if (menu === "creerBoutique") {
      setActiveSubItem("settings");
      setMobileStep("labels");
      if (!isMobile) setDesktopStep("component");
      return;
    }

    if (menu === "operations") {
      if (isEntreprise) {
        setActiveSubItem(null);
        setMobileStep("operations-main");
        setDesktopStep("operations-main");
      } else {
        setActiveMainMenu("ventes");
        setActiveSubItem(null);
        setMobileStep("labels");
        setDesktopStep("labels");
        setMobileTitle("MaBoutik");
      }
      return;
    }

    if (isMobile) {
      setMobileStep("main");
      setMobileTitle("MaBoutik");
    }
  };

  const handleLabelClick = (id) => {
    if (id === "sites") {
      setActiveSubItem(null);
      setMobileStep("sites"); 
      setDesktopStep("sites");
      setMobileTitle("Boutiques");
      return;
    }

    if (id === "depenses-main") {
      setActiveMainMenu("depenses");
      setActiveSubItem(null);
      setMobileStep("labels");
      setDesktopStep("labels");
      setMobileTitle("Dépenses");
      return;
    }

    const categoryKeys = ["ventes", "gestion"]; 
    if (categoryKeys.includes(id)) {
      setActiveMainMenu(id);
      setActiveSubItem(null);
      setMobileStep("labels");
      setDesktopStep("labels");
      setMobileTitle(id.charAt(0).toUpperCase() + id.slice(1));
      return;
    }

    const componentIds = [
      "sales", "sales-list", "products", "stocks", "add-expense", "expenses",
      "managers", "clients", "clients-debiteurs", "encaissement", "transfert",
      "archive-ventes", "archive-debiteurs", "archive-depenses", 
      "apports-exploitant", "banque", 
    ];

    if (componentIds.includes(id)) {
      setActiveSubItem(id);
      const allLabels = Object.values(labels).flat();
      const found = allLabels.find((l) => l.id === id);
      if (found) setMobileTitle(found.label);
      if (!isMobile) setDesktopStep("component");
      return;
    }
  };

  const handleSelectSite = (site) => {
    selectSite(site);
    setSelectedSite(site);
    setActiveSubItem(null);
    setActiveMainMenu(null);
    setMobileStep("after-site");
    setDesktopStep("after-site");
    setMobileTitle("Catégories");
  };

  const handleBackClick = () => {
    if (isGestionnaire && activeMainMenu === "ventes" && mobileStep === "labels" && activeSubItem === null) {
      return;
    }
    
    if (activeSubItem) {
      const previousMenu = activeMainMenu || "main";
      setActiveSubItem(null);

      if (["home-dashboard", "settings"].includes(activeSubItem)) {
        setActiveMainMenu(null);
        setMobileStep("main");
        setDesktopStep("main");
        setMobileTitle("MaBoutik");
        return;
      }
      
      if (["apports-exploitant", "banque", "archive-depenses"].includes(activeSubItem) || activeMainMenu === "depenses") {
        setActiveMainMenu("operations");
        setMobileStep("operations-main");
        setDesktopStep("operations-main");
        setMobileTitle("Opérations");
        return;
      }
      
      setDesktopStep("labels");
      setMobileStep("labels");
      let menuTitle = previousMenu.charAt(0).toUpperCase() + previousMenu.slice(1);
      if (previousMenu === "home" || previousMenu === "creerBoutique") {
        menuTitle = sidebarMenu.find(m => m.id === previousMenu)?.label || "MaBoutik";
      } else if (previousMenu === "operations" || previousMenu === "ventes") {
          menuTitle = "MaBoutik";
      }
      setMobileTitle(menuTitle);
      return;
    }

    if (mobileStep === "labels" || desktopStep === "labels") {
      if (["ventes", "gestion"].includes(activeMainMenu) && selectedSite) {
        setActiveMainMenu(null);
        setMobileStep("after-site");
        setDesktopStep("after-site");
        setMobileTitle("Catégories");
        return;
      }

      if (activeMainMenu === "depenses") {
        setActiveMainMenu("operations");
        setMobileStep("operations-main");
        setDesktopStep("operations-main");
        setMobileTitle("Opérations");
        return;
      }

      setActiveMainMenu(null);
      setMobileStep("main");
      setDesktopStep("main");
      setMobileTitle("MaBoutik");
      return;
    }

    if (mobileStep === "after-site" || desktopStep === "after-site") {
      setSelectedSite(null);
      selectSite(null);
      setMobileStep("sites");
      setDesktopStep("sites");
      setMobileTitle("Boutiques");
      return;
    }

    if (mobileStep === "sites" || desktopStep === "sites") {
      setActiveMainMenu("operations");
      setMobileStep("operations-main");
      setDesktopStep("operations-main");
      setMobileTitle("Opérations");
      return;
    }

    if (mobileStep === "operations-main" || desktopStep === "operations-main") {
      setActiveMainMenu(null);
      setMobileStep("main");
      setDesktopStep("main");
      setMobileTitle("MaBoutik");
      return;
    }

    setMobileStep("main");
    setDesktopStep("main");
    setActiveSubItem(null);
    setActiveMainMenu(null);
    setMobileTitle("MaBoutik");
  };

  const renderMenuLabels = (menuKey) => {
    let items = labels[menuKey];
    if (!items?.length) return <p className="text-muted text-center py-5 animate-fade-in">Aucun élément disponible</p>;

    const gridClassMap = {
      operationMain: "menu-labels-grid-2x2",
      ventes: isGestionnaire && items.length === 7 ? "menu-labels-grid-ventes-gestionnaire" : "menu-labels-grid-3x3",
      afterSite: "menu-labels-grid-2x1", // Nouvelle classe pour aligner 2 éléments sur une ligne
      gestion: "menu-labels-grid-2x1"   // Nouvelle classe pour aligner 2 éléments sur une ligne
    };

    const gridClass = gridClassMap[menuKey] || "menu-labels-grid";

    return (
      <div className={`${gridClass} ${isMobile ? 'mobile-grid-layout' : ''}`}>
        {items.map((it, index) => (
          <button
            key={it.id}
            className={`menu-label-card card-color-${it.color} animate-pop-in ${isGestionnaire && it.id === 'transfert' ? 'full-width-card' : ''}`}
            style={{ animationDelay: `${index * 0.05}s` }}
            onClick={() => handleLabelClick(it.id)}
          >
            <div className="card-icon-wrapper">{it.icon}</div>
            <span className="card-label">{it.label}</span>
          </button>
        ))}
      </div>
    );
  };

  const renderActiveComponent = () => {
    if (!user) return <div className="loading-state d-flex justify-content-center align-items-center"><FaSpinner className="spin h2" /></div>;

    const withBackButton = (component) => (
      <div className="component-container animate-slide-up">
        {((!isMobile && (activeSubItem !== null || desktopStep !== "main")) || (isMobile && mobileStep !== "main")) && (
          <button className="btn btn-light shadow-sm mb-4 d-flex align-items-center gap-2 back-btn-custom" onClick={handleBackClick}>
            <FaArrowLeft /> <span>Retour</span>
          </button>
        )}
        <div className="glass-panel p-3 p-md-4">
          {component}
        </div>
      </div>
    );

    if (activeSubItem) {
      const componentMap = {
        settings: <Settings entrepriseId={entrepriseId} />,
        "home-dashboard": <Home entrepriseId={entrepriseId} siteId={effectiveSiteId} entrepriseNom={entrepriseNom} typeUser={typeUser} date_creation={gestionnaireDateCreation} />,
        sales: <SalesManager entrepriseId={entrepriseId} siteId={effectiveSiteId} entrepriseNom={entrepriseNom} gestionnaireId={gestionnaireId} date_creation={gestionnaireDateCreation} />,
        "sales-list": <SalesList entrepriseId={entrepriseId} siteId={effectiveSiteId} entrepriseNom={entrepriseNom} typeUser={typeUser} date_creation={gestionnaireDateCreation} />,
        products: <Product entrepriseId={entrepriseId} siteId={effectiveSiteId} entrepriseNom={entrepriseNom} date_creation={gestionnaireDateCreation} />,
        stocks: <Stock entrepriseId={entrepriseId} siteId={effectiveSiteId} entrepriseNom={entrepriseNom} date_creation={gestionnaireDateCreation} />,
        "add-expense": <AddExpense entrepriseId={entrepriseId} siteId={effectiveSiteId} entrepriseNom={entrepriseNom} date_creation={gestionnaireDateCreation} />,
        expenses: <ExpenseList entrepriseId={entrepriseId} siteId={effectiveSiteId} entrepriseNom={entrepriseNom} date_creation={gestionnaireDateCreation} />,
        managers: <Gestionnaire entrepriseId={entrepriseId} siteId={effectiveSiteId} date_creation={gestionnaireDateCreation} />,
        clients: <Clients entrepriseId={entrepriseId} siteId={effectiveSiteId} date_creation={gestionnaireDateCreation} />,
        "clients-debiteurs": <ClientsDebiteurs entrepriseId={entrepriseId} siteId={effectiveSiteId} typeUser={typeUser} date_creation={gestionnaireDateCreation} />,
        encaissement: <Encaissement entrepriseId={entrepriseId} siteId={effectiveSiteId} date_creation={gestionnaireDateCreation} />,
        transfert: <Transfert entrepriseId={entrepriseId} siteId={effectiveSiteId} gestionnaireId={gestionnaireId} date_creation={gestionnaireDateCreation} />,
        "apports-exploitant": <ApportsExploitant entrepriseId={entrepriseId} siteId={effectiveSiteId} date_creation={gestionnaireDateCreation} />,
        banque: <Banque entrepriseId={entrepriseId} siteId={effectiveSiteId} />,
        "archive-ventes": <ArchiveVentes entrepriseId={entrepriseId} siteId={effectiveSiteId} />,
        "archive-debiteurs": <ArchiveDebiteurs entrepriseId={entrepriseId} siteId={effectiveSiteId} />,
        "archive-depenses": <ArchiveDepenses entrepriseId={entrepriseId} siteId={effectiveSiteId} />,
      };
      return withBackButton(componentMap[activeSubItem]);
    }

    if (isEntreprise) {
      if (mobileStep === "operations-main" || desktopStep === "operations-main") {
        return withBackButton(
          <div className="text-center">
            <h4 className="fw-bold mb-4 text-dark-emphasis">Tableau des Opérations</h4>
            {renderMenuLabels("operationMain")} 
          </div>
        );
      }
      if (mobileStep === "sites" || desktopStep === "sites") {
        return withBackButton(<SiteSelector entrepriseId={entrepriseId} onSelect={handleSelectSite} />);
      }
      if (selectedSite && (mobileStep === "after-site" || desktopStep === "after-site")) {
        return withBackButton(
          <div className="text-center">
            <div className="badge bg-primary px-3 py-2 mb-2 rounded-pill">Site : {selectedSite?.nom}</div>
            <h4 className="fw-bold mb-4 text-dark-emphasis">Sélectionnez une catégorie</h4>
            {renderMenuLabels("afterSite")}
          </div>
        );
      }
      if (activeMainMenu && (mobileStep === "labels" || desktopStep === "labels")) {
        return withBackButton(
          <div className="text-center">
            <h4 className="fw-bold mb-4 text-dark-emphasis text-capitalize">{activeMainMenu}</h4>
            {renderMenuLabels(activeMainMenu)}
          </div>
        );
      }
      if (mobileStep === "main" && isMobile) {
        return (
          <div className="mobile-main-container animate-fade-in">
            <div className="user-card-header shadow-sm border-0 card mb-4 w-100">
              <div className="card-body d-flex align-items-center gap-3">
                <div className="avatar-circle bg-primary text-white"><FaUser /></div>
                <div>
                  <div className="fw-bold text-dark">{entrepriseNom}</div>
                  <small className="text-muted">Tableau de bord principal</small>
                </div>
              </div>
            </div>
            <div className="mobile-vertical-menu w-100 px-2">
              {sidebarMenu.map((item, idx) => (
                <button 
                  key={item.id} 
                  className="mobile-menu-item shadow-sm animate-pop-in" 
                  style={{ animationDelay: `${idx * 0.1}s` }}
                  onClick={() => handleMenuClick(item.id)}
                >
                  <span className="icon-box bg-primary-subtle text-primary">{item.icon}</span>
                  <span className="label-text">{item.label}</span>
                </button>
              ))}
            </div>
          </div>
        );
      }
    }

    if (isGestionnaire && (mobileStep === "labels" || desktopStep === "labels") && activeMainMenu === "ventes") {
      return (
        <div className="container-fluid animate-fade-in">
          <div className="text-center mb-4 desktop-only">
             <h3 className="fw-bold text-dark">Espace Gestionnaire</h3>
          </div>
          {renderMenuLabels("ventes")}
        </div>
      );
    }

    return (
      <div className="desktop-welcome-screen text-center animate-fade-in">
        <div className="welcome-illustration mb-4">
          <img src={LOGO_PATH} alt="Logo" className="floating-logo" />
        </div>
        <h2 className="display-6 fw-bold text-dark">Bienvenue, <span className="text-primary">{user?.nom}</span></h2>
        <p className="text-muted fs-5">Prêt à gérer votre boutique aujourd'hui ?</p>
        <div className="d-flex justify-content-center gap-3 mt-5">
           <div className="stat-pill">Activité: <span className="text-success fw-bold">En ligne</span></div>
           <div className="stat-pill">Role: <span className="text-primary fw-bold">{typeUser}</span></div>
        </div>
      </div>
    );
  };

  if (!isLoggedIn) {
    return (
      <Routes>
        <Route path="/" element={<Welcome />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/verify-code" element={<VerifyCode />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <div className={`app-layout ${sidebarOpen ? "sidebar-expanded" : ""} ${isGestionnaire ? "gestionnaire-mode" : ""}`}>
      <header className="navbar-top shadow-sm">
        <div className="container-fluid h-100 d-flex align-items-center justify-content-between px-3">
          <div className="d-flex align-items-center gap-2">
            {!isMobile && isEntreprise && (
              <button className="btn-toggle-sidebar me-2" onClick={() => setSidebarOpen(!sidebarOpen)}>
                <FaBars />
              </button>
            )}
            {isMobile && mobileStep !== "main" && (
              <button className="btn-back-mobile" onClick={handleBackClick}>
                <FaArrowLeft />
              </button>
            )}
            <div className="topbar-branding d-flex align-items-center gap-2">
              <img src={LOGO_PATH} alt="MaBoutik" className="brand-img" />
              <h1 className="h5 mb-0 fw-bold brand-name">{isMobile ? mobileTitle : "MaBoutik"}</h1>
            </div>
          </div>
          
          <button className="btn-logout-custom" onClick={handleLogout} disabled={isLoggingOut}>
            {isLoggingOut ? <FaSpinner className="spin" /> : <><FaSignOutAlt /> <span className="d-none d-md-inline">Déconnexion</span></>}
          </button>
        </div>
      </header>

      {isEntreprise && !isMobile && (
        <aside className={`main-sidebar ${!sidebarOpen ? "is-collapsed" : ""}`}>
          <div className="sidebar-header-branding">
            <img src={LOGO_PATH} alt="Logo" className="sidebar-brand-logo" />
            <span className="sidebar-brand-text">MaBoutik</span>
          </div>
          <div className="sidebar-content">
            <div className="sidebar-profile-card">
              <div className="profile-avatar"><FaUser /></div>
              <div className="profile-details">
                <div className="name text-truncate">{user?.nom}</div>
                <div className="role text-truncate text-primary-emphasis">{entrepriseNom}</div>
              </div>
            </div>
            <nav className="nav flex-column sidebar-nav mt-3">
              {sidebarMenu.map((m) => (
                <button 
                  key={m.id} 
                  className={`nav-link-custom ${activeMainMenu === m.id ? "active" : ""}`} 
                  onClick={() => handleMenuClick(m.id)}
                >
                  <span className="nav-icon">{m.icon}</span>
                  <span className="nav-text">{m.label}</span>
                </button>
              ))}
            </nav>
          </div>
          <div className="sidebar-footer">
            <button className="btn-sidebar-logout" onClick={handleLogout}>
              <FaSignOutAlt /> <span>Fermer la session</span>
            </button>
          </div>
        </aside>
      )}

      <main className="content-viewport">
        <div className="container-fluid p-0 p-md-4">
          {renderActiveComponent()}
        </div>
      </main>
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppContent/>
    </AuthProvider>
  );
}